'use client';

import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Icon, type IconName } from '../../icons';
import type { FilterDefinition, FilterOperator, OPERATORS_FOR_TYPE } from '@/types/tableViews';
import { OPERATORS_FOR_TYPE as OPS_FOR_TYPE } from '@/types/tableViews';

import './FilterDropdown.css';

export interface FilterDropdownProps {
  definitions: FilterDefinition[];
  open: boolean;
  onClose: () => void;
  onAddFilter: (filterKey: string, operator: FilterOperator, value: string | string[]) => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

export function FilterDropdown({
  definitions,
  open,
  onClose,
  onAddFilter,
  anchorRef,
  className = '',
}: FilterDropdownProps) {
  const [search, setSearch] = useState('');
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [submenuSearch, setSubmenuSearch] = useState('');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const hoveredItemRef = useRef<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<'right' | 'left'>('right');

  useEffect(() => {
    if (open) {
      setSearch('');
      setHoveredKey(null);
      setSubmenuSearch('');
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPosition(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const top = rect.bottom + 4;
    let left = rect.left;
    const panelWidth = 240;
    if (left + panelWidth > window.innerWidth - 8) {
      left = window.innerWidth - panelWidth - 8;
    }
    setPosition({ top, left });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (submenuRef.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown, true);
    };
  }, [open, onClose]);

  const filtered = definitions.filter((d) =>
    d.label.toLowerCase().includes(search.toLowerCase()),
  );

  const groups = new Map<string, FilterDefinition[]>();
  for (const def of filtered) {
    const g = def.group ?? '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(def);
  }

  const hoveredDef = definitions.find((d) => d.key === hoveredKey);

  const handleSelectOption = useCallback(
    (def: FilterDefinition, optionValue: string) => {
      const defaultOp = OPS_FOR_TYPE[def.type]?.[0] ?? 'is';
      onAddFilter(def.key, defaultOp, optionValue);
      onClose();
    },
    [onAddFilter, onClose],
  );

  const handleSelectDate = useCallback(
    (def: FilterDefinition, dateValue: string) => {
      onAddFilter(def.key, 'after', dateValue);
      onClose();
    },
    [onAddFilter, onClose],
  );

  const handleTextSubmit = useCallback(
    (def: FilterDefinition, text: string) => {
      if (!text.trim()) return;
      onAddFilter(def.key, 'contains', text.trim());
      onClose();
    },
    [onAddFilter, onClose],
  );

  if (!open) return null;

  const iconForDef = (def: FilterDefinition) => {
    const iconMap: Record<string, string> = {
      select: 'Circle',
      multi_select: 'CircleDot',
      date: 'Calendar',
      text: 'Type',
      boolean: 'ToggleLeft',
    };
    return (def.icon as string) || iconMap[def.type] || 'Filter';
  };

  return createPortal(
    <>
      <motion.div
        ref={panelRef}
        className={`filter-dropdown ${className}`.trim()}
        style={position ? { position: 'fixed', top: position.top, left: position.left } : undefined}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.14 }}
        onMouseLeave={(e) => {
          const related = e.relatedTarget;
          if (related instanceof Node && submenuRef.current?.contains(related)) return;
          setHoveredKey(null);
        }}
      >
        <div className="filter-dropdown__search">
          <Icon name="Search" size={14} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Add Filter..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="filter-dropdown__search-input"
          />
        </div>
        <div className="filter-dropdown__list">
          {Array.from(groups.entries()).map(([groupName, defs], gi) => (
            <React.Fragment key={groupName || '__default'}>
              {gi > 0 && <div className="filter-dropdown__separator" />}
              {defs.map((def) => (
                <button
                  key={def.key}
                  className={`filter-dropdown__item ${hoveredKey === def.key ? 'filter-dropdown__item--active' : ''}`}
                  onMouseEnter={(e) => {
                    hoveredItemRef.current = e.currentTarget;
                    setHoveredKey(def.key);
                    setSubmenuSearch('');
                  }}
                  onClick={(e) => {
                    hoveredItemRef.current = e.currentTarget;
                    if (def.type === 'boolean') {
                      onAddFilter(def.key, 'is', 'true');
                      onClose();
                    } else if (!def.options?.length && def.type === 'text') {
                      setHoveredKey(def.key);
                    } else {
                      setHoveredKey(def.key);
                    }
                  }}
                  type="button"
                >
                  <span className="filter-dropdown__item-icon">
                    <Icon name={iconForDef(def) as IconName} size={16} />
                  </span>
                  <span className="filter-dropdown__item-label">{def.label}</span>
                  {(def.options !== undefined || def.type === 'date' || def.type === 'text') && (
                    <span className="filter-dropdown__item-chevron">
                      <Icon name="ChevronRight" size={14} />
                    </span>
                  )}
                </button>
              ))}
            </React.Fragment>
          ))}
          {filtered.length === 0 && (
            <div className="filter-dropdown__empty">No filters found</div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {hoveredDef && (hoveredDef.options !== undefined || hoveredDef.type === 'date' || hoveredDef.type === 'text') && (
          <FilterSubmenu
            ref={submenuRef}
            definition={hoveredDef}
            parentRef={panelRef}
            hoveredItemRef={hoveredItemRef}
            submenuSearch={submenuSearch}
            onSubmenuSearchChange={setSubmenuSearch}
            onSelectOption={(val) => handleSelectOption(hoveredDef, val)}
            onSelectDate={(val) => handleSelectDate(hoveredDef, val)}
            onTextSubmit={(val) => handleTextSubmit(hoveredDef, val)}
            onMouseLeave={(e) => {
              const related = e.relatedTarget;
              if (related instanceof Node && panelRef.current?.contains(related)) return;
              setHoveredKey(null);
            }}
          />
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
}

interface FilterSubmenuProps {
  definition: FilterDefinition;
  parentRef: React.RefObject<HTMLDivElement | null>;
  hoveredItemRef: React.RefObject<HTMLButtonElement | null>;
  submenuSearch: string;
  onSubmenuSearchChange: (value: string) => void;
  onSelectOption: (value: string) => void;
  onSelectDate: (value: string) => void;
  onTextSubmit: (value: string) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

const FilterSubmenu = React.forwardRef<HTMLDivElement, FilterSubmenuProps>(
  function FilterSubmenu(
    { definition, parentRef, hoveredItemRef, submenuSearch, onSubmenuSearchChange, onSelectOption, onSelectDate, onTextSubmit, onMouseLeave },
    ref,
  ) {
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const [textValue, setTextValue] = useState('');
    const searchRef = useRef<HTMLInputElement | null>(null);

    useLayoutEffect(() => {
      if (!parentRef.current) return;
      const parentRect = parentRef.current.getBoundingClientRect();
      const itemRect = hoveredItemRef.current?.getBoundingClientRect();
      let left = parentRect.right - 8;
      const submenuWidth = 220;
      if (left + submenuWidth > window.innerWidth - 8) {
        left = parentRect.left - submenuWidth + 8;
      }
      const top = itemRect ? itemRect.top : parentRect.top;
      setPos({ top, left });
    }, [parentRef, hoveredItemRef, definition.key]);

    useEffect(() => {
      requestAnimationFrame(() => searchRef.current?.focus());
    }, [definition.key]);

    const filteredOptions = definition.options?.filter((o) =>
      o.label.toLowerCase().includes(submenuSearch.toLowerCase()),
    );

    const datePresets = [
      { label: '1 week ago', value: getRelativeDate(-7) },
      { label: '2 weeks ago', value: getRelativeDate(-14) },
      { label: '1 month ago', value: getRelativeDate(-30) },
      { label: '3 months ago', value: getRelativeDate(-90) },
      { label: '6 months ago', value: getRelativeDate(-180) },
      { label: '1 year ago', value: getRelativeDate(-365) },
    ];

    return (
      <motion.div
        ref={ref}
        className="filter-submenu"
        style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : undefined}
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -4 }}
        transition={{ duration: 0.12 }}
        onMouseLeave={onMouseLeave}
      >
        {definition.type === 'text' ? (
          <div className="filter-submenu__text">
            <input
              ref={searchRef}
              type="text"
              className="filter-submenu__text-input"
              placeholder={`Search ${definition.label.toLowerCase()}...`}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onTextSubmit(textValue);
              }}
            />
          </div>
        ) : definition.type === 'date' ? (
          <div className="filter-submenu__dates">
            {datePresets.map((preset) => (
              <button
                key={preset.value}
                className="filter-submenu__date-option"
                onClick={() => onSelectDate(preset.value)}
                type="button"
              >
                {preset.label}
              </button>
            ))}
            <div className="filter-dropdown__separator" />
            <div className="filter-submenu__custom-date">
              <input
                type="date"
                className="filter-submenu__date-input"
                onChange={(e) => {
                  if (e.target.value) onSelectDate(e.target.value);
                }}
              />
            </div>
          </div>
        ) : (
          <>
            {(definition.searchable || (definition.options?.length ?? 0) > 5) && (
              <div className="filter-submenu__search">
                <Icon name="Search" size={14} />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder={`Search ${definition.label.toLowerCase()}...`}
                  aria-label={`Search ${definition.label.toLowerCase()}`}
                  value={submenuSearch}
                  onChange={(e) => onSubmenuSearchChange(e.target.value)}
                  className="filter-submenu__search-input"
                />
              </div>
            )}
            <div className="filter-submenu__options">
              {filteredOptions?.map((opt) => (
                <button
                  key={opt.value}
                  className="filter-submenu__option"
                  data-activity-label={`${definition.label} filter option`}
                  onClick={() => onSelectOption(opt.value)}
                  type="button"
                >
                  <span className="filter-submenu__option-label">{opt.label}</span>
                  {opt.count !== undefined && (
                    <span className="filter-submenu__option-count">{opt.count}</span>
                  )}
                </button>
              ))}
              {filteredOptions?.length === 0 && (
                <div className="filter-dropdown__empty" role="status">{definition.optionsMessage ?? 'No results'}</div>
              )}
            </div>
          </>
        )}
      </motion.div>
    );
  },
);

function getRelativeDate(daysOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().split('T')[0];
}
